class ProductCalculatorApp extends HTMLElement {
  static get observedAttributes() {
    return ['data-calc-mode', 'data-uom'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-calc-mode') {
      this.calcMode = newValue || 'area';
      this.updateCalculatorUI();
    }
    if (name === 'data-uom') {
      this.updateCalculatorUI();
    }
  }

  constructor() {
    super();
    this.sectionId = this.getAttribute('section-id');
    this.calcMode = this.getAttribute('data-calc-mode') || 'area';
    this.weightMultiplier = parseFloat(this.getAttribute('data-weight-multiplier')) || 1.0;

    // Drawer internal elements
    this.lengthInput = this.querySelector('.calc-input-length');
    this.widthInput = this.querySelector('.calc-input-width');
    this.heightInput = this.querySelector('.calc-input-height');
    this.depthInput = this.querySelector('.calc-input-depth');
    this.amountDisplay = this.querySelector('.calc-required-amount');
    this.updateBtn = this.querySelector('.calc-update-qty-btn');
    this.closeBtn = this.querySelector('.calc-close-btn');

    // External elements in the main product section
    this.section = document.querySelector(`.hdt-secion-main-product--${this.sectionId}`) || document.body;
    this.wastageDropdown = this.section.querySelector(`#allow_wastage_main_${this.sectionId}`);
    this.variantRows = this.section.querySelectorAll('.variant-row');

    // Application state
    this.amountRequired = 0;

    // The main inline container and its toggle button
    this.inlineContainer = document.getElementById(`calculator-inline-${this.sectionId}`);
    this.toggleBtn = this.section.querySelector(`#calc-toggle-btn-${this.sectionId}`);

    this.overlay = document.getElementById(`calc-overlay-${this.sectionId}`);

    // Delay binding slightly to ensure section is fully parsed
    setTimeout(() => {
      this.updateCalculatorUI();
      this.bindEvents();
      this.updateCalculatorAvailability();
    }, 100);
  }

  // ─── Modal open / close with body-lock and overlay ─────────────────────────
  openModal() {
    if (!this.inlineContainer) return;
    if (this.inlineContainer.parentElement !== document.body) {
      document.body.appendChild(this.inlineContainer);
    }
    if (this.overlay && this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }
    this.inlineContainer.style.display = 'block';
    if (this.overlay) this.overlay.classList.add('sl-calc-overlay--active');
    document.body.classList.add('sl-calc-open');
    this.inlineContainer.focus ? this.inlineContainer.focus() : null;
  }

  closeModal() {
    if (!this.inlineContainer) return;
    this.inlineContainer.style.display = 'none';
    if (this.overlay) this.overlay.classList.remove('sl-calc-overlay--active');
    document.body.classList.remove('sl-calc-open');
  }

  // ─── Helper: parse m²/lm/m³ per pack from a variant row ────────────────────
  getM2PerPack(row) {
    if (row.getAttribute('data-dims-confirmed') === 'false') return 0;

    const parentApp = row.closest('product-calculator-app');
    const uom = parentApp ? parentApp.getAttribute('data-uom') : '';
    const calcMode = parentApp ? parentApp.getAttribute('data-calc-mode') : '';

    if (calcMode === 'linear') {
      let lmPack = parseFloat(row.getAttribute('data-lm-pack')) || 0;
      if (lmPack > 0) return lmPack;
    } else if (calcMode === 'volume') {
      let m3Pack = parseFloat(row.getAttribute('data-m3-pack')) || 0;
      if (m3Pack > 0) return m3Pack;

      const densityKgPerM3 = parseFloat(parentApp?.getAttribute('data-density-kg-m3')) || 0;
      let weightGrams = parseFloat(row.getAttribute('data-weight')) || 0;
      let weightKg = weightGrams / 1000;
      if (weightKg > 0 && densityKgPerM3 > 0) {
        return weightKg / densityKgPerM3;
      }
    } else {
      let m2Pack = parseFloat(row.getAttribute('data-m2-pack')) || 0;
      if (m2Pack > 0) return m2Pack;
    }

    const explicitCoverage = parseFloat(row.getAttribute('data-m2')) || 0;
    if (explicitCoverage > 0) return explicitCoverage;

    if (uom === 'kg' || uom === 'per_kg') {
      if (this.weightMultiplier > 1.0) {
        return 1.0 / this.weightMultiplier;
      }
      let weightGrams = parseFloat(row.getAttribute('data-weight')) || 0;
      if (weightGrams <= 0) return 0;
      return weightGrams / 1000;
    } else if (uom === 'tonne' || uom === 'per_tonne') {
      if (this.weightMultiplier > 1.0) {
        return 1000.0 / this.weightMultiplier;
      }
      let weightGrams = parseFloat(row.getAttribute('data-weight')) || 0;
      if (weightGrams <= 0) return 0;
      return weightGrams / 1000000;
    }

    let m2PerPack = 0;

    if (m2PerPack <= 0 || isNaN(m2PerPack) || (calcMode === 'linear' && m2PerPack === 1.0)) {
      const titleText = row.querySelector('td:first-child')
        ? row.querySelector('td:first-child').innerText.toLowerCase()
        : '';

      // e.g. "15.3m2" or "15.3m²" or "15.3m3" or "15.3m³"
      const match = titleText.match(/([\d\.]+)\s*m(?:2|²|3|³)/i);
      if (match) {
        m2PerPack = parseFloat(match[1]);
      } else {
        // e.g. "600x600mm" or "600×600mm"
        const dimMatch = titleText.match(/(\d{2,4})\s*[xX\u00d7]\s*(\d{2,4})/);
        if (dimMatch) {
          const w = parseFloat(dimMatch[1]);
          const h = parseFloat(dimMatch[2]);
          if (w >= 10 && h >= 10) {
            if (calcMode === 'linear') {
              m2PerPack = w / 1000.0;
            } else {
              m2PerPack = (w * h) / 1_000_000.0;
            }
          }
        }
      }
    }

    // Return 0 (not 1) when dimensions cannot be resolved. The Liquid
    // template already hides the .variant-m2-display element for these
    // rows, so setM2Display is a no-op. Returning 1 would silently inflate
    // the coverage summary for any variant without confirmed dimensions.
    return m2PerPack > 0 ? m2PerPack : 0;
  }

  setM2Display(row, value) {
    if (!row) return;
    const m2El = row.querySelector('.variant-m2-display, .variant-m2-input');
    if (!m2El) return;
    if (m2El.tagName === 'INPUT' || m2El.tagName === 'TEXTAREA') {
      m2El.value = value;
    } else {
      const isEmpty = !value || value === '' || value === '-' || value === 0 || value === '0';
      let unit = 'm²';
      if (this.calcMode === 'volume') unit = 'm³';
      if (this.calcMode === 'linear') unit = 'm';
      m2El.textContent = isEmpty ? '-' : `${value} ${unit}`;
      m2El.classList.remove('sl-covers-active');
      m2El.style.backgroundColor = '#f5f3ef';
      m2El.style.color = isEmpty ? '#888' : '#333';
      m2El.style.borderColor = '#d6d0ca';
    }
  }

  updateCalculatorAvailability() {
    const hasCalculableVariant = Array.from(this.variantRows || []).some(row => {
      const title = row.querySelector('.variant-row-title')?.textContent.toLowerCase() || '';
      const qtyInput = row.querySelector('.product-calculator-qty-input');
      return !title.includes('sample') && qtyInput && this.getM2PerPack(row) > 0;
    });

    if (hasCalculableVariant) return;
    if (this.inlineContainer) this.inlineContainer.style.display = 'none';
    [this.toggleBtn, this.updateBtn].forEach(button => {
      if (!button) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('title', 'Calculator unavailable until product measurements are confirmed');
    });
  }

  bindEvents() {
    if (this.toggleBtn && this.inlineContainer) {
      this.toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const isHidden = this.inlineContainer.style.display === 'none';
        if (isHidden) {
          this.openModal();
        } else {
          this.closeModal();
        }
        this.hideSuccessSummary();
      });
    }

    if (this.closeBtn && this.inlineContainer) {
      this.closeBtn.addEventListener('click', () => {
        this.closeModal();
        this.hideSuccessSummary();
      });
    }

    // Overlay click to dismiss
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeModal();
        this.hideSuccessSummary();
      });
    }

    // ESC key to dismiss
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.inlineContainer && this.inlineContainer.style.display !== 'none') {
        this.closeModal();
        this.hideSuccessSummary();
      }
    });

    const inputHandler = () => {
      this.calculateRequired();
      this.hideSuccessSummary();
    };

    const autoSelectOnFocus = (e) => {
      if (e.target && typeof e.target.select === 'function') {
        e.target.select();
      }
    };

    [this.lengthInput, this.widthInput, this.heightInput, this.depthInput].forEach(input => {
      if (input) {
        input.addEventListener('input', inputHandler);
        input.addEventListener('focus', autoSelectOnFocus);
        input.addEventListener('click', autoSelectOnFocus);
      }
    });

    if (this.wastageDropdown) {
      this.wastageDropdown.addEventListener('change', () => {
        if (this.modalWastageDropdown) this.modalWastageDropdown.value = this.wastageDropdown.value;
        this.calculateRequired();
        this.recalculateInlineRows();
        this.hideSuccessSummary();
      });
    }

    this.modalWastageDropdown = this.querySelector(`#allow_wastage_modal_${this.sectionId}`);
    if (this.modalWastageDropdown) {
      this.modalWastageDropdown.addEventListener('change', () => {
        if (this.wastageDropdown) this.wastageDropdown.value = this.modalWastageDropdown.value;
        this.calculateRequired();
        this.recalculateInlineRows();
        this.hideSuccessSummary();
      });
    }

    // Bind table row inputs and row click sync
    if (this.variantRows) {
      this.variantRows.forEach(row => {
        const qtyInput = row.querySelector('.product-calculator-qty-input');
        const variantId = row.getAttribute('data-variant-id');
        const m2PerPack = this.getM2PerPack(row);

        // Click on variant row activates row & updates main quantity max
        row.addEventListener('click', (e) => {
          if (!e.target.closest('.product-calculator-qty-input')) {
            this.highlightVariantRow(variantId);
            const packs = qtyInput ? (parseInt(qtyInput.value) || 0) : 0;
            this.syncMainProductForm(variantId, packs);
          }
        });

        if (qtyInput) {
          qtyInput.addEventListener('input', (e) => {
            this.hideSuccessSummary();
            let val = e.target.value.trim();
            if (/^0\d+/.test(val)) {
              val = val.replace(/^0+/, '');
              e.target.value = val;
            }
            if (val === '') {
              this.setM2Display(row, '-');
              this.syncMainProductForm(variantId, 0);
              this.highlightVariantRow(variantId);
              this.updateSubtotal();
              this.enforceStockLimits(row, 0);
              return;
            }
            let packs = parseInt(val) || 0;
            const allowedPacks = this.enforceStockLimits(row, packs);
            if (allowedPacks !== packs) {
              packs = allowedPacks;
              qtyInput.value = packs;
            }
            const calculatedCovers = packs > 0 ? (packs * m2PerPack).toFixed(2) : '-';
            this.setM2Display(row, calculatedCovers);

            this.syncMainProductForm(variantId, packs);
            this.highlightVariantRow(variantId);
            this.updateSubtotal();
          });
          qtyInput.addEventListener('focus', () => {
            this.highlightVariantRow(variantId);
            const packs = parseInt(qtyInput.value) || 0;
            this.syncMainProductForm(variantId, packs);
            this.updateSubtotal();
          });
        }
      });
    }

    if (this.updateBtn) {
      this.updateBtn.addEventListener('click', () => {
        this.updateQuantity();
      });
    }

    // Hook into Kalles/T4S cart added event to reset/close UI
    document.addEventListener('t4s:cart:added', () => {
      if (this.inlineContainer && this.inlineContainer.style.display !== 'none') {
        this.closeModal();
        if (this.lengthInput) this.lengthInput.value = '';
        if (this.widthInput) this.widthInput.value = '';
        if (this.heightInput) this.heightInput.value = '';
        if (this.depthInput) this.depthInput.value = '';
        this.calculateRequired();
      }
    });

    // Listen for variant change events from section pickers
    const variantChangeHandler = (variantId) => {
      if (variantId) {
        this.highlightVariantRow(variantId);
        this.hideSuccessSummary();
      }
    };

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      if (variant) {
        this.updateMainPricePerUom(variant);
        variantChangeHandler(variant.id);
      }
    });

    // Listen for changes on main product form variant selectors (radios, selects, swatches)
    const pickerEls = this.section.querySelectorAll('select[name="id"], input[name="id"], .hdt-variant-picker input, .hdt-variant-swatch input');
    pickerEls.forEach(el => {
      el.addEventListener('change', (e) => {
        const vId = e.target.value || e.target.getAttribute('data-variant-id');
        if (vId) variantChangeHandler(vId);
      });
    });

    // Bind bidirectional sync to the main quantity input
    const mainQtyInput = this.section.querySelector('input[name="quantity"]');
    if (mainQtyInput) {
      const mainQtyHandler = () => {
        this.hideSuccessSummary();
        const activeRow = this.section.querySelector('.variant-row.active-variant');
        if (activeRow) {
          const qtyInput = activeRow.querySelector('.product-calculator-qty-input');
          let newQty = parseInt(mainQtyInput.value) || 0;
          const allowedQty = this.enforceStockLimits(activeRow, newQty);
          if (allowedQty !== newQty) {
            newQty = allowedQty;
            mainQtyInput.value = newQty;
          }
          if (qtyInput && parseInt(qtyInput.value) !== newQty) {
            qtyInput.value = newQty;
            const m2PerPack = this.getM2PerPack(activeRow);
            const calculatedCovers = newQty > 0 ? (newQty * m2PerPack).toFixed(2) : '-';
            this.setM2Display(activeRow, calculatedCovers);
            this.updateSubtotal();
          }
        }
      };
      mainQtyInput.addEventListener('input', mainQtyHandler);
      mainQtyInput.addEventListener('change', mainQtyHandler);
    }

    // Initial highlight & subtotal sync
    const initialVariantId = this.section.querySelector('input[name="id"], select[name="id"]')?.value;
    if (initialVariantId) {
      this.highlightVariantRow(initialVariantId);
      
      // Sync main quantity to the active variant row on load
      if (mainQtyInput) {
        let initialQty = parseInt(mainQtyInput.value) || 0;
        const activeRow = this.section.querySelector('.variant-row.active-variant');
        if (initialQty > 0 && activeRow) {
          const allowedQty = this.enforceStockLimits(activeRow, initialQty);
          if (allowedQty !== initialQty) {
            initialQty = allowedQty;
            mainQtyInput.value = initialQty;
          }
          const qtyInput = activeRow.querySelector('.product-calculator-qty-input');
          if (qtyInput) {
            qtyInput.value = initialQty;
            const m2PerPack = this.getM2PerPack(activeRow);
            const calculatedCovers = initialQty > 0 ? (initialQty * m2PerPack).toFixed(2) : '-';
            this.setM2Display(activeRow, calculatedCovers);
          }
        }
      }
    }

    // Intercept form submit to support adding multiple variants from the table at once via AJAX
    const prForm = this.section.querySelector('.hdt-main-product-form');
    if (prForm) {
      prForm.addEventListener('submit', (e) => {
        const itemsToAdd = [];
        if (this.variantRows) {
          this.variantRows.forEach(row => {
            const qtyInput = row.querySelector('.product-calculator-qty-input');
            if (qtyInput) {
              const qty = parseInt(qtyInput.value) || 0;
              if (qty > 0) {
                const variantId = row.getAttribute('data-variant-id');
                itemsToAdd.push({
                  id: parseInt(variantId),
                  quantity: qty
                });
              }
            }
          });
        }

        if (itemsToAdd.length > 0) {
          e.preventDefault();
          e.stopPropagation();

          const submitBtn = prForm.querySelector('[type="submit"][name="add"]');
          const errorWrapper = prForm.querySelector('.hdt-form__message-wrapper');
          const errorText = prForm.querySelector('.hdt-error-message');
          
          if (errorWrapper) errorWrapper.setAttribute('hidden', '');

          if (submitBtn) {
            submitBtn.setAttribute('disabled', 'true');
            submitBtn.setAttribute('aria-busy', 'true');
          }

          // Get sectionId of cart drawer to request shopify section rendering
          const cartDrawerEl = document.querySelector('[ref="hdt-cart"]');
          const drawerSectionId = cartDrawerEl ? cartDrawerEl.getAttribute('section-id') : 'cart-drawer';

          // Shopify /cart/add.js has a hard limit of 50 units per line item per request.
          // Chunk large quantities into sequential batches of ≤50 to work around this.
          const SHOPIFY_QTY_LIMIT = 50;

          const chunkItems = (items) => {
            const batches = [];
            items.forEach(item => {
              let remaining = item.quantity;
              while (remaining > 0) {
                const batchQty = Math.min(remaining, SHOPIFY_QTY_LIMIT);
                batches.push([{ id: item.id, quantity: batchQty }]);
                remaining -= batchQty;
              }
            });
            return batches;
          };

          const batches = chunkItems(itemsToAdd);
          const needsChunking = batches.length > itemsToAdd.length;

          // Chain all batches sequentially; only send sections on the final request
          const runBatches = async (allBatches) => {
            let lastData = null;
            for (let i = 0; i < allBatches.length; i++) {
              const isLast = i === allBatches.length - 1;
              const body = {
                items: allBatches[i],
                sections: isLast && drawerSectionId ? [drawerSectionId] : []
              };
              const response = await fetch(Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(body)
              });
              lastData = await response.json();
              // Stop early if any batch returns an error
              if (lastData.status || lastData.description) break;
            }
            return lastData;
          };

          runBatches(batches)
          .then(data => {
            if (submitBtn) {
              submitBtn.removeAttribute('disabled');
              submitBtn.removeAttribute('aria-busy');
            }

            if (data.status || data.description) {
              if (errorWrapper && errorText) {
                errorText.textContent = data.message || data.description;
                errorWrapper.removeAttribute('hidden');
              }
              this.syncRowsFromCart(itemsToAdd);
            } else {
              // Success! Clear inputs in the table
              let addedSummaryHtml = '';
              if (data.items && data.items.length > 0) {
                data.items.forEach(item => {
                  addedSummaryHtml += `<div style="margin-bottom: 4px;">• <strong>${item.quantity}x</strong> ${item.product_title} - ${item.variant_title}</div>`;
                });
              } else if (data.quantity) {
                addedSummaryHtml = `<div style="margin-bottom: 4px;">• <strong>${data.quantity}x</strong> ${data.product_title} - ${data.variant_title}</div>`;
              }
              
              const addedSummaryContainer = this.section.querySelector('.sl-added-items-summary');
              const addedSummaryList = this.section.querySelector('.sl-added-items-list');
              if (addedSummaryContainer && addedSummaryList && addedSummaryHtml) {
                addedSummaryList.innerHTML = addedSummaryHtml;
                addedSummaryContainer.style.display = 'block';
              }

              if (this.variantRows) {
                this.variantRows.forEach(row => {
                  const qtyInput = row.querySelector('.product-calculator-qty-input');
                  if (qtyInput) {
                    qtyInput.value = '';
                    this.updateQtySelectorClass(qtyInput);
                  }
                  this.setM2Display(row, '-');
                });
              }
              this.updateSubtotal();

              // Trigger theme's cart:update event so the cart drawer updates and opens!
              const cartUpdateEvent = new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  cartData: data,
                  source: 'product-form-component',
                  actionAfterATC: 'open_cart_drawer'
                }
              });
              document.dispatchEvent(cartUpdateEvent);
            }
          })
          .catch(err => {
            console.error('Add to cart error:', err);
            if (submitBtn) {
              submitBtn.removeAttribute('disabled');
              submitBtn.removeAttribute('aria-busy');
            }
          });

        } else {
          // If no items have quantity, prevent form submission and show error
          e.preventDefault();
          e.stopPropagation();
          const errorWrapper = prForm.querySelector('.hdt-form__message-wrapper');
          const errorText = prForm.querySelector('.hdt-error-message');
          if (errorWrapper && errorText) {
            errorText.textContent = 'Please enter a quantity for at least one option.';
            errorWrapper.removeAttribute('hidden');
          }
        }
      }, true);
    }

    // Course toggle for brick/paviors laying patterns
    const toggles = this.querySelectorAll(`input[name="laying_mode_${this.sectionId}"]`);
    if (toggles.length > 0) {
      toggles.forEach(radio => {
        radio.addEventListener('change', (e) => {
          const mode = e.target.value;
          this.setAttribute('data-calc-mode', mode);
        });
      });
    }

    this.updateSubtotal();
  }

  enforceStockLimits(row, packs) {
    const title = row.querySelector('td:first-child')?.innerText.toLowerCase() || '';
    if (title.includes('sample')) {
      return packs;
    }

    const management = row.getAttribute('data-inventory-management');
    const policy = row.getAttribute('data-inventory-policy');
    const stockQty = parseInt(row.getAttribute('data-inventory-quantity')) || 0;
    const enableStockLimit = row.getAttribute('data-enable-stock-limit') === 'true';

    let isLimited = false;
    if (enableStockLimit && management === 'shopify' && policy === 'deny') {
      if (packs > stockQty) {
        isLimited = true;
      }
    } else if (management === 'shopify' && policy === 'deny' && stockQty <= 0) {
      isLimited = true;
    }

    let warningEl = row.querySelector('.sl-stock-limit-warning');
    if (isLimited && stockQty > 0) {
      if (!warningEl) {
        warningEl = document.createElement('div');
        warningEl.className = 'sl-stock-limit-warning';
        warningEl.style.cssText = 'color: #d93838; font-size: 11px; font-weight: 500; margin-top: 4px; text-align: center;';
        const qtyTd = row.querySelector('.product-calculator-qty-input')?.parentNode;
        if (qtyTd) {
          qtyTd.appendChild(warningEl);
        }
      }
      warningEl.textContent = `Only ${stockQty} in stock. Quantity adjusted.`;
    } else {
      if (warningEl) {
        warningEl.remove();
      }
    }

    return isLimited ? (stockQty > 0 ? stockQty : 0) : packs;
  }

  async syncRowsFromCart(requestedItems) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      const drawerSectionId = cartDrawer?.getAttribute('section-id');
      if (drawerSectionId) {
        const sectionsResponse = await fetch(
          `${Shopify.routes.root}?sections=${encodeURIComponent(drawerSectionId)}`,
          { headers: { 'Accept': 'application/json' } }
        );
        if (sectionsResponse.ok) {
          cart.sections = await sectionsResponse.json();
        }
      }

      this.variantRows.forEach(row => {
        const variantId = String(row.getAttribute('data-variant-id'));
        if (!requestedIds.has(variantId)) return;

        const cartItem = cart.items.find(item => String(item.variant_id) === variantId);
        const actualQuantity = cartItem ? cartItem.quantity : 0;
        const qtyInput = row.querySelector('.product-calculator-qty-input');
        if (qtyInput) {
          qtyInput.value = actualQuantity > 0 ? actualQuantity : '';
          this.updateQtySelectorClass(qtyInput);
        }

        const covers = actualQuantity > 0
          ? (actualQuantity * this.getM2PerPack(row)).toFixed(2)
          : '-';
        this.setM2Display(row, covers);

        if (row.classList.contains('active-variant')) {
          this.syncMainProductForm(variantId, actualQuantity);
        }
      });

      this.updateSubtotal();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cartData: cart,
          source: 'product-calculator',
          actionAfterATC: 'open_cart_drawer'
        }
      }));
    } catch (error) {
      console.error('Cart reconciliation error:', error);
    }
  }

  recalculateInlineRows() {
    if (!this.variantRows) return;
    this.variantRows.forEach(row => {
      const qtyInput = row.querySelector('.product-calculator-qty-input');
      const variantId = row.getAttribute('data-variant-id');
      const m2PerPack = this.getM2PerPack(row);

      if (qtyInput && qtyInput.value !== '') {
        let packs = parseInt(qtyInput.value) || 0;
        const calculatedCovers = packs > 0 ? (packs * m2PerPack).toFixed(2) : '-';
        this.setM2Display(row, calculatedCovers);
        this.syncMainProductForm(variantId, packs);
      }
    });
    this.updateSubtotal();
  }

  syncMainProductForm(variantId, packs) {
    const mainQtyInput = this.section.querySelector('input[name="quantity"]');
    if (mainQtyInput) {
      mainQtyInput.value = packs;
      mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const idInput = this.section.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const idSelect = this.section.querySelector('select[name="id"]');
    if (idSelect) {
      idSelect.value = variantId;
      idSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update the URL ?variant= parameter so the browser reflects the correct variant
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* noop */ }

    // Dispatch the theme's variant:change custom event so the gallery & pickers update
    try {
      const activeRow = this.section.querySelector(`.variant-row[data-variant-id="${variantId}"]`);
      if (activeRow) {
        const variantData = {
          id: parseInt(variantId),
          title: activeRow.querySelector('.variant-row-title')?.textContent.trim() || '',
          price: parseFloat(activeRow.getAttribute('data-price')) || 0,
          available: (activeRow.getAttribute('data-inventory-policy') === 'continue') ||
                     (parseInt(activeRow.getAttribute('data-inventory-quantity')) > 0),
          featured_image: activeRow.getAttribute('data-featured-image-src')
            ? { src: activeRow.getAttribute('data-featured-image-src') } : null,
        };
        this.section.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variantData }
        }));
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variantData }
        }));
      }
    } catch (e) { /* noop */ }
  }

  updateCalculatorUI() {
    const showWidth = ['area', 'volume', 'layer-or-area'].includes(this.calcMode);
    const showHeight = ['cladding'].includes(this.calcMode);
    const showDepth = ['volume'].includes(this.calcMode);

    const fLength = this.querySelector('.sl-field-length');
    const fWidth = this.querySelector('.sl-field-width');
    const fHeight = this.querySelector('.sl-field-height');
    const fDepth = this.querySelector('.sl-field-depth');

    if (fLength) fLength.style.display = 'flex';
    if (fWidth) {
      fWidth.style.display = showWidth ? 'flex' : 'none';
      if (!showWidth && this.widthInput) this.widthInput.value = '';
    }
    if (fHeight) {
      fHeight.style.display = showHeight ? 'flex' : 'none';
      if (!showHeight && this.heightInput) this.heightInput.value = '';
    }
    if (fDepth) {
      fDepth.style.display = showDepth ? 'flex' : 'none';
      if (!showDepth && this.depthInput) this.depthInput.value = '';
    }

    // Dynamic field borders
    const fields = [fLength, fWidth, fHeight, fDepth];
    const visibleFields = fields.filter(f => f && f.style.display !== 'none');
    visibleFields.forEach((field, index) => {
      if (index === visibleFields.length - 1) {
        field.style.borderRight = 'none';
      } else {
        field.style.borderRight = '1px solid #d6d0ca';
      }
    });

    // Update headers in variant table
    const tableHeaderUom = this.section.querySelector('.sl-table-header-uom');
    if (tableHeaderUom) {
      let label = 'm²';
      if (this.calcMode === 'volume') label = 'm³';
      else if (this.calcMode === 'linear') label = 'm';
      else if (this.calcMode === 'cladding') label = 'm² face';
      tableHeaderUom.textContent = `Covers (${label})`;
    }

    // Update metric display unit suffix
    const amountUnitEl = this.querySelector('.calc-required-unit');
    if (amountUnitEl) {
      let label = 'm²';
      if (this.calcMode === 'volume') label = 'm³';
      else if (this.calcMode === 'linear') label = 'm';
      amountUnitEl.textContent = label;
    }

    // Update header labels for table variants
    if (this.variantRows) {
      this.variantRows.forEach(row => {
        const variantM2Suffix = row.querySelector('.variant-m2-suffix');
        if (variantM2Suffix) {
          let label = 'm²';
          if (this.calcMode === 'volume') label = 'm³';
          else if (this.calcMode === 'linear') label = 'm';
          variantM2Suffix.textContent = label;
        }
      });
    }

    // Update instructions text
    const descText = this.querySelector('.hdt-calculator-content p');
    if (descText) {
      if (this.calcMode === 'volume') {
        descText.textContent = 'Enter the length, width, and depth of the area to calculate the volume required.';
      } else if (this.calcMode === 'linear') {
        descText.textContent = 'Enter the linear length to calculate the amount required.';
      } else if (this.calcMode === 'cladding') {
        descText.textContent = 'Enter the length and height of the wall to calculate the face area required.';
      } else if (this.calcMode === 'layer-or-area') {
        descText.textContent = 'Enter the length and width of the area to be covered. The calculator will work out how many packs are required.';
      } else {
        descText.textContent = 'Enter the length and width of the area to cover to calculate the amount of paving required.';
      }
    }

    this.calculateRequired();
    this.recalculateInlineRows();
  }

  calculateRequired() {
    const l = parseFloat(this.lengthInput ? this.lengthInput.value : 0) || 0;
    let baseAmount = 0;

    if (this.calcMode === 'linear') {
      baseAmount = l;
    } else if (this.calcMode === 'volume') {
      const w = parseFloat(this.widthInput ? this.widthInput.value : 0) || 0;
      const d = parseFloat(this.depthInput ? this.depthInput.value : 0) || 0;
      baseAmount = l * w * (d / 1000); // depth is in mm
    } else if (this.calcMode === 'cladding') {
      const h = parseFloat(this.heightInput ? this.heightInput.value : 0) || 0;
      baseAmount = l * h;
    } else if (this.calcMode === 'layer-or-area') {
      const w = parseFloat(this.widthInput ? this.widthInput.value : 0) || 0;
      baseAmount = l * w;
    } else {
      const w = parseFloat(this.widthInput ? this.widthInput.value : 0) || 0;
      baseAmount = l * w;
    }

    if (this.wastageDropdown) {
      const wPercent = parseFloat(this.wastageDropdown.value) || 0;
      baseAmount = baseAmount * (1 + (wPercent / 100));
    }

    this.amountRequired = Math.max(0, baseAmount);

    if (this.amountDisplay) {
      this.amountDisplay.textContent = this.amountRequired.toFixed(2);
    }
  }

  // ─── Smart single-variant quantity selection ────────────────────────────────
  // Picks the currently selected variant in option selectors, or falls back to
  // the option that results in the fewest packs needed to cover amountRequired.
  updateQuantity() {
    if (this.amountRequired <= 0) return;
    if (!this.variantRows || this.variantRows.length === 0) return;

    // Build option list (skip sample/no-qty rows)
    const options = [];
    this.variantRows.forEach(row => {
      const title = row.querySelector('.variant-row-title')?.textContent.toLowerCase() || '';
      if (title.includes('sample')) return; // skip sample rows
      const qtyInput = row.querySelector('.product-calculator-qty-input');
      if (!qtyInput) return; // sample row — no qty input
      const m2Input = row.querySelector('.variant-m2-input');
      const variantId = row.getAttribute('data-variant-id');
      const m2PerPack = this.getM2PerPack(row);
      if (!Number.isFinite(m2PerPack) || m2PerPack <= 0) return;
      options.push({ row, m2PerPack, variantId, qtyInput, m2Input });
    });

    if (options.length === 0) return;

    // Try to lock onto currently active variant size
    const activeVariantId = this.section.querySelector('input[name="id"], select[name="id"]')?.value;
    let bestOption = null;
    let bestPacks = 0;

    if (activeVariantId) {
      bestOption = options.find(opt => String(opt.variantId) === String(activeVariantId));
    }

    if (bestOption) {
      bestPacks = Math.ceil(this.amountRequired / bestOption.m2PerPack);
    } else {
      // Sort descending by pack size fallback
      options.sort((a, b) => b.m2PerPack - a.m2PerPack);
      bestOption = options[0];
      bestPacks = Math.ceil(this.amountRequired / bestOption.m2PerPack);

      for (const opt of options) {
        const packs = Math.ceil(this.amountRequired / opt.m2PerPack);
        if (packs < bestPacks) {
          bestPacks = packs;
          bestOption = opt;
        }
      }
    }

    if (!bestOption || !Number.isFinite(bestPacks) || bestPacks <= 0) return;
    bestPacks = this.enforceStockLimits(bestOption.row, bestPacks);

    // Apply: set chosen row qty, zero all others
    options.forEach(opt => {
      if (opt === bestOption) {
        opt.qtyInput.value = bestPacks;
        this.updateQtySelectorClass(opt.qtyInput);
        const calculatedCovers = bestPacks > 0 ? (bestPacks * opt.m2PerPack).toFixed(2) : '-';
        this.setM2Display(opt.row, calculatedCovers);
      } else {
        opt.qtyInput.value = '';
        this.updateQtySelectorClass(opt.qtyInput);
        this.setM2Display(opt.row, '-');
      }
    });

    // Sync main product form to the chosen variant
    this.syncMainProductForm(bestOption.variantId, bestPacks);
    this.highlightVariantRow(bestOption.variantId);
    this.updateSubtotal();

    // Close the inline form
    if (this.inlineContainer) {
      this.inlineContainer.style.display = 'none';
    }
  }

  highlightVariantRow(variantId) {
    if (!this.variantRows) return;
    this.variantRows.forEach(row => {
      if (row.getAttribute('data-variant-id') === String(variantId)) {
        row.classList.add('active-variant');
        this.updateMainQtyInputMax(row);

        // Auto-switch product gallery image when variant has a featured image
        const featuredImageSrc = row.getAttribute('data-featured-image-src');
        if (featuredImageSrc) {
          // Try multiple gallery selectors used by T4S / Kalles themes
          const gallerySelectors = [
            '.hdt-main-media img',
            '.hdt-media-slide.is-selected img',
            '.hdt-media-slide img',
            '.hdt-media-item img',
            '[data-hdt-media-main] img',
            '.hdt-product-media-main img',
            '.product-media-container img',
            '.hdt-featured-media img',
          ];
          let mainImg = null;
          for (const sel of gallerySelectors) {
            mainImg = this.section.querySelector(sel);
            if (mainImg) break;
          }
          if (mainImg) {
            mainImg.src = featuredImageSrc;
            mainImg.srcset = featuredImageSrc;
            if (mainImg.hasAttribute('data-src')) {
              mainImg.setAttribute('data-src', featuredImageSrc);
            }
          }
        }
      } else {
        row.classList.remove('active-variant');
      }
    });
  }

  updateMainQtyInputMax(activeRow) {
    if (!activeRow) return;
    const mainQtyInput = this.section.querySelector('input[name="quantity"]');
    if (!mainQtyInput) return;

    const title = activeRow.querySelector('td:first-child')?.innerText.toLowerCase() || '';
    const management = activeRow.getAttribute('data-inventory-management');
    const policy = activeRow.getAttribute('data-inventory-policy');
    const stockQty = parseInt(activeRow.getAttribute('data-inventory-quantity')) || 0;

    if (title.includes('sample') || management !== 'shopify' || policy === 'continue') {
      mainQtyInput.removeAttribute('max');
      mainQtyInput.removeAttribute('data-max');
    } else if (management === 'shopify' && policy === 'deny') {
      if (stockQty > 0) {
        mainQtyInput.setAttribute('max', stockQty);
        mainQtyInput.setAttribute('data-max', stockQty);
      } else {
        mainQtyInput.setAttribute('max', 0);
        mainQtyInput.setAttribute('data-max', 0);
      }
    } else {
      mainQtyInput.removeAttribute('max');
      mainQtyInput.removeAttribute('data-max');
    }
  }

  zeroOutOtherRows(activeVariantId) {
    if (!this.variantRows) return;
    this.variantRows.forEach(row => {
      const rowVariantId = row.getAttribute('data-variant-id');
      if (String(rowVariantId) !== String(activeVariantId)) {
        const qtyInput = row.querySelector('.product-calculator-qty-input');
        if (qtyInput) {
          qtyInput.value = '';
          this.updateQtySelectorClass(qtyInput);
        }
        this.setM2Display(row, '-');
      }
    });
  }

  updateSubtotal() {
    let totalCents = 0;
    const summaryParts = [];
    let hasActiveQty = false;

    if (this.variantRows) {
      this.variantRows.forEach(row => {
        const qtyInput = row.querySelector('.product-calculator-qty-input');
        if (qtyInput) {
          const qty = parseInt(qtyInput.value) || 0;
          const priceCents = parseFloat(row.getAttribute('data-price')) || 0;
          const rowSubtotalCents = qty * priceCents;
          totalCents += rowSubtotalCents;

          const rowSubtotalEl = row.querySelector('.variant-row-subtotal');
          if (rowSubtotalEl) {
            const formattedRowSubtotal = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(rowSubtotalCents / 100);
            rowSubtotalEl.textContent = qty > 0 ? formattedRowSubtotal : '£0.00';
          }

          if (qty > 0) {
            hasActiveQty = true;
            const variantTitle = row.querySelector('td:first-child')?.innerText || '';
            const m2PerPack = this.getM2PerPack(row);
            const actualM2 = qty * m2PerPack;

            // Determine unit label for coverage value display
            const uom = this.getAttribute('data-uom') || '';
            let unitLabel = 'm²';
            if (this.calcMode === 'volume') unitLabel = 'm³';
            else if (this.calcMode === 'linear') unitLabel = 'm';
            else if (this.calcMode === 'cladding') unitLabel = 'm² (face)';

            let qtyLabel = '';
            if (uom === 'kg' || uom === 'per_kg') qtyLabel = ' kg';
            else if (uom === 'tonne' || uom === 'per_tonne') qtyLabel = ' tonne';

            let part = `${qty}${qtyLabel} × ${variantTitle}`;
            if (actualM2 > 0) {
              part += ` (covers ~${actualM2.toFixed(2)} ${unitLabel})`;
            }
            summaryParts.push(part);
          }
        }
      });
    }

    const subtotalEl = this.section.querySelector('.sl-table-subtotal-val');
    if (subtotalEl) {
      const formatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalCents / 100);
      subtotalEl.textContent = formatted;
    }

    // Update main order summary at the bottom if present
    const summaryContainer = this.section.querySelector('.sl-order-summary');
    const summaryTextEl = this.section.querySelector('.sl-order-summary-text');
    const mainSubtotalEl = this.section.querySelector('.sl-buy-button-subtotal-val');

    if (summaryContainer && summaryTextEl) {
      if (hasActiveQty && summaryParts.length > 0) {
        summaryTextEl.innerHTML = summaryParts.join('<br>');
        summaryContainer.style.display = 'block';
      } else {
        summaryContainer.style.display = 'none';
      }
    }

    if (mainSubtotalEl) {
      const formatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalCents / 100);
      mainSubtotalEl.textContent = formatted;
    }

    // Dynamic submit button state (ADD TO CART vs Out-of-Stock Enquiry)
    const submitBtn = this.section.querySelector('button[type="submit"][name="add"]');
    const btnTextEl = this.section.querySelector('.hdt-btn-atc_text');
    const orderBlock = this.section.querySelector('.sl-order-block');

    if (hasActiveQty) {
      if (submitBtn) {
        submitBtn.removeAttribute('disabled');
        submitBtn.style.setProperty('display', 'inline-flex', 'important');
        if (btnTextEl) {
          btnTextEl.textContent = 'ADD TO CART';
        }
      }
      if (orderBlock) {
        orderBlock.style.setProperty('display', 'none', 'important');
      }
    } else {
      if (submitBtn && btnTextEl) {
        if (btnTextEl.textContent !== 'ADD TO CART' && btnTextEl.textContent !== 'Add to cart') {
          btnTextEl.textContent = 'ADD TO CART';
        }
      }
      if (orderBlock) {
        orderBlock.style.removeProperty('display');
      }
    }
  }

  // Update the main price per UOM dynamically when variant changes
  updateMainPricePerUom(variant) {
    const uomElements = this.section.querySelectorAll('.sl-main-price-per-uom');
    if (!uomElements.length) return;

    const titleDown = (variant.title || '').toLowerCase();
    const isSample = titleDown.includes('sample');

    let packSize = 0;
    // Find the corresponding table row to get the exact pack size
    const row = this.section.querySelector(`.variant-row[data-variant-id="${variant.id}"]`);
    if (row) {
      packSize = this.getM2PerPack(row);
    } else {
      // Fallback: parse from title if row is not found
      if (titleDown.includes('m2')) {
        const match = titleDown.match(/([\d\.]+)\s*m(?:2|²)/i);
        if (match) packSize = parseFloat(match[1]) || 0;
      } else if (titleDown.includes('m3')) {
        const match = titleDown.match(/([\d\.]+)\s*m3/i);
        if (match) packSize = parseFloat(match[1]) || 0;
      } else if (titleDown.includes('lm')) {
        const match = titleDown.match(/([\d\.]+)\s*lm/i);
        if (match) packSize = parseFloat(match[1]) || 0;
      }
    }

    const uom = this.getAttribute('data-uom') || '';
    uomElements.forEach(uomEl => {
      if (isSample || packSize === 1.0 || packSize <= 0) {
        uomEl.style.display = 'none';
      } else {
        const unitPrice = (variant.price / 100) / packSize;
        const formatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(unitPrice);
        let label = 'm²';
        if (this.calcMode === 'volume') label = 'm³';
        else if (this.calcMode === 'linear') label = 'm';
        else if (this.calcMode === 'cladding') label = 'm² (face)';
        else if (this.weightMultiplier <= 1.0) {
          if (uom === 'kg' || uom === 'per_kg') label = 'kg';
          else if (uom === 'tonne' || uom === 'per_tonne') label = 'tonne';
        }
        
        uomEl.textContent = `(${formatted} / ${label})`;
        uomEl.style.display = '';
      }
    });
  }

  hideSuccessSummary() {
    const container = this.section.querySelector('.sl-added-items-summary');
    if (container) {
      container.style.display = 'none';
    }
  }

  updateQtySelectorClass(input) {
    if (!input) return;
    const parent = input.parentElement;
    if (parent && parent.classList.contains('hdt-quantity-wrapper')) {
      if (parseInt(input.value) > 0) {
        parent.classList.add('is-active');
      } else {
        parent.classList.remove('is-active');
      }
    }
  }
}

if (!customElements.get('product-calculator-app')) {
  customElements.define('product-calculator-app', ProductCalculatorApp);
}
